import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChatTokenService } from 'src/app/services/chat-token.service';

const CHAT_DOMAINS: Record<'voxloud' | 'romulus', string> = {
  voxloud: 'https://chat.voxloud.com',
  romulus: 'https://chat.romulus.live',
};

const CHAT_ERROR_KEYS: Record<string, string> = {
  CHAT_ACCOUNT_DISABLED: 'chat.error_disabled',
  COMPANY_NOT_FOUND: 'chat.error_not_found',
};

const NON_RETRYABLE_CODES = ['CHAT_ACCOUNT_DISABLED', 'COMPANY_NOT_FOUND'];

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
})
export class ChatComponent implements OnInit, OnDestroy {
  constructor(
    private sanitizer: DomSanitizer,
    private router: Router,
    private tokensService: ChatTokenService
  ) {}

  public iframeURL: SafeResourceUrl;
  public errorKey: string | null = null;
  public isRetryable = false;
  public isLoading = false;
  private provider: 'voxloud' | 'romulus';
  private readonly destroy$ = new Subject<void>();
  private tokenRequest?: Subscription;

  ngOnDestroy(): void {
    this.tokenRequest?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    const state = this.router.getCurrentNavigation()?.extras?.state ?? history.state;
    const provider = state?.provider;
    if (provider === 'voxloud' || provider === 'romulus') {
      this.provider = provider;
      this.fetchToken();
    } else {
      this.errorKey = 'chat.error_generic';
      this.isRetryable = false;
    }
  }

  public refresh(): void {
    this.fetchToken();
  }

  public iframeError(): void {
    this.errorKey = 'chat.error_generic';
    this.isRetryable = true;
  }

  private fetchToken(): void {
    this.tokenRequest?.unsubscribe();
    this.errorKey = null;
    this.isRetryable = false;
    this.iframeURL = null;
    this.isLoading = true;

    const request$ =
      this.provider === 'voxloud' ? this.tokensService.getVoxloudChatToken() : this.tokensService.getRomulusChatToken();

    this.tokenRequest = request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ token }) => {
        this.isLoading = false;
        const domain = CHAT_DOMAINS[this.provider];
        const query = new URLSearchParams({ token }).toString();
        this.iframeURL = this.sanitizer.bypassSecurityTrustResourceUrl(
          `${domain}/api-login?fullscreen=1&redirect_uri=/inbox-merged/chats&${query}`
        );
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading = false;
        const code = err.error?.errors?.[0]?.code;
        this.errorKey = CHAT_ERROR_KEYS[code] ?? 'chat.error_generic';
        this.isRetryable = !NON_RETRYABLE_CODES.includes(code);
      },
    });
  }
}
